'use client';

import { styled } from "@linaria/react";
import { Color, Mesh, Program, Renderer, Triangle } from "ogl";
import { useEffect, useRef } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { usePrefersIncreasedContrast, usePrefersReducedMotion, usePrefersReducedTransparency } from "../../hooks/usePrefersXYZ";
import { MOBILE_MEDIA_QUERY_WITH_HEIGHT } from "../global.linaria.global";
import "./background.linaria.module.css!=!./background?./background.linaria.module.css?css=H4sIAAAAAAAAA62Q0WrDMAxF3/cVpjCIoQ522Ppgf42cKIlpagVbabeG/fsoaaBbBx1soAdxuZKObtmZUJ/H12pmGq12A7ZstTuFhntrtH52PYau56UfKQcOFC34TMPE6M4qxAbfrDJOndDvA6s2DIzJ+nSZi5hzocudFDVFTpC5MGUl3a9M60ZOEK+HaYQ68LuoskDIqEJUNLF77LjKVruPcv3Z9pCLGuIRspxXg7k1eGwp4Xz/9wUUI9uN2Lgvya2BmB8y3CU8OA/1vks0xcYOISIk1SVoAkYumIQnZjpsReo8FHorljLyu6LLl3tNylt2aBnTA/Tl3L/RM41/QH/6BA/kMOGNAgAA"
const GalaxyContainer = /*#__PURE__*/styled('div')({
  name: "GalaxyContainer",
  class: "g1iczp52",
  propsAsIs: false
});
const vertexShader = `
attribute vec2 uv;
attribute vec2 position;

varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 0, 1);
}
`;
function getFragmentShader(layers: `${bigint}`) {
  return `
precision highp float;

uniform float uTime;
uniform vec3 uResolution;
uniform vec2 uFocal;
uniform vec2 uRotation;
uniform float uStarSpeed;
uniform float uDensity;
uniform float uHueShift;
uniform float uSpeed;
uniform vec2 uMouse;
uniform float uGlowIntensity;
uniform float uSaturation;
uniform bool uMouseRepulsion;
uniform float uTwinkleIntensity;
uniform float uRotationSpeed;
uniform float uRepulsionStrength;
uniform float uMouseActiveFactor;
uniform float uAutoCenterRepulsion;
uniform bool uTransparent;
uniform bool NUM_LAYER;

varying vec2 vUv;

#define NUM_LAYER ${layers}.0
#define STAR_COLOR_CUTOFF 0.2
#define MAT45 mat2(0.7071, -0.7071, 0.7071, 0.7071)
#define PERIOD 3.0

float Hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float tri(float x) {
  return abs(fract(x) * 2.0 - 1.0);
}

float tris(float x) {
  float t = fract(x);
  return 1.0 - smoothstep(0.0, 1.0, abs(2.0 * t - 1.0));
}

float trisn(float x) {
  float t = fract(x);
  return 2.0 * (1.0 - smoothstep(0.0, 1.0, abs(2.0 * t - 1.0))) - 1.0;
}

vec3 hsv2rgb(vec3 c) {
  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}

float Star(vec2 uv, float flare) {
  float d = pow(log(length(uv) * 0.2 + 1.0),0.4) + 0.8 * length(uv);
  float m = (0.17 * uGlowIntensity) / d;
  float rays = smoothstep(0.0, 1.0, 1.0 - abs(uv.x * uv.y * 1000.0));
  m += rays * flare * uGlowIntensity;
  uv *= MAT45;
  rays = smoothstep(0.0, 1.0, 1.0 - abs(uv.x * uv.y * 1000.0));
  m += rays * 0.3 * flare * uGlowIntensity;
  m *= smoothstep(1.0, 0.2, d);
  return pow(m, 1.8);
}

vec3 StarLayer(vec2 uv) {
  vec3 col = vec3(0);

  vec2 gv = fract(uv) - 0.5;
  vec2 id = floor(uv);

  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 offset = vec2(float(x), float(y));
      vec2 si = id + vec2(float(x), float(y));
      float seed = Hash21(si);
      float size = fract(seed * 345.32) * 0.8;
      float glossLocal = tri(uStarSpeed / (PERIOD * seed + 1.0));
      float flareSize = smoothstep(0.9, 1.0, size) * glossLocal * 9.0;

      float red = smoothstep(STAR_COLOR_CUTOFF, 1.0, Hash21(si + 1.0)) + STAR_COLOR_CUTOFF;
      float blu = smoothstep(STAR_COLOR_CUTOFF, 1.0, Hash21(si + 3.0)) + STAR_COLOR_CUTOFF;
      float grn = min(red, blu) * seed;
      vec3 base = vec3(red, grn, blu);

      float hue = atan(base.g - base.r, base.b - base.r) / (2.0 * 3.14159) + 0.5;
      hue = fract(hue + uHueShift / 360.0);
      float sat = length(base - vec3(dot(base, vec3(0.299, 0.587, 0.114)))) * uSaturation;
      float val = max(max(base.r, base.g), base.b);
      base = hsv2rgb(vec3(hue, sat, val));

      vec2 pad = vec2(tris(seed * 34.0 + uTime * uSpeed / 10.0), tris(seed * 38.0 + uTime * uSpeed / 30.0)) - 0.5;

      float star = Star(gv - offset - pad, flareSize);
      vec3 color = base;

      float twinkle = trisn(uTime * uSpeed + seed * 6.2831) * 0.5 + 1.0;
      twinkle = mix(1.0, twinkle, uTwinkleIntensity);
      star *= twinkle;

      col += log(star * size * color * 1.2 + 1.0);
    }
  }

  return col;
}

void main() {
  vec2 focalPx = uFocal * uResolution.xy;
  vec2 uv = (vUv * uResolution.xy - focalPx) / uResolution.y;

  vec2 mouseNorm = uMouse - vec2(0.5);

  if (uAutoCenterRepulsion > 0.0) {
    vec2 centerUV = vec2(0.0, 0.0); // Center in UV space
    float centerDist = length(uv - centerUV);
    vec2 repulsion = normalize(uv - centerUV) * (uAutoCenterRepulsion / (centerDist + 0.1));
    uv += repulsion * 0.05;
  } else if (uMouseRepulsion) {
    vec2 mousePosUV = (uMouse * uResolution.xy - focalPx) / uResolution.y;
    float mouseDist = length(uv - mousePosUV);
    vec2 repulsion = normalize(uv - mousePosUV) * (uRepulsionStrength / (mouseDist + 0.1));
    uv += repulsion * 0.05 * uMouseActiveFactor;
  } else {
    vec2 mouseOffset = mouseNorm * 0.1 * uMouseActiveFactor;
    uv += mouseOffset;
  }

  float autoRotAngle = uTime * uRotationSpeed;
  mat2 autoRot = mat2(cos(autoRotAngle), -sin(autoRotAngle), sin(autoRotAngle), cos(autoRotAngle));
  uv = autoRot * uv;

  uv = mat2(uRotation.x, -uRotation.y, uRotation.y, uRotation.x) * uv;

  vec3 col = vec3(0.05);

  for (float i = 0.0; i < 1.0; i += 1.0 / NUM_LAYER) {
    float depth = fract(i + uStarSpeed * uSpeed);
    float scale = mix(20.0 * uDensity, 0.5 * uDensity, depth);
    float fade = depth * smoothstep(1.0, 0.9, depth);
    col += StarLayer(uv * scale + i * 453.32) * fade;
  }

  if (uTransparent) {
    float alpha = length(col);
    alpha = smoothstep(0.0, 0.3, alpha); // Enhance contrast
    alpha = min(alpha, 1.0); // Clamp to maximum 1.0
    gl_FragColor = vec4(col, alpha);
  } else {
    gl_FragColor = vec4(col, 1.0);
  }
}
`;
}
interface GalaxyProps {
  readonly focal?: readonly [number, number];
  readonly rotation?: readonly [number, number];
  readonly starSpeed?: number;
  readonly density?: number;
  readonly hueShift?: number;
  readonly disableAnimation?: boolean;
  readonly speed?: number;
  readonly mouseInteraction?: boolean;
  readonly glowIntensity?: number;
  readonly saturation?: number;
  readonly mouseRepulsion?: boolean;
  readonly twinkleIntensity?: number;
  readonly rotationSpeed?: number;
  readonly repulsionStrength?: number;
  readonly autoCenterRepulsion?: number;
  readonly transparent?: boolean;
}
export default function GalaxyBackground(props: GalaxyProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobileSized = useMediaQuery(MOBILE_MEDIA_QUERY_WITH_HEIGHT);
  if (isMobileSized || prefersReducedMotion) return null;
  return <GalaxyBackgroundRaw {...props} />;
}
const DEFAULT_FOCAL = [0.3, 0] as const;
const DEFAULT_ROTATION = [1.0, 0.0] as const;
function GalaxyBackgroundRaw({
  focal = DEFAULT_FOCAL,
  rotation = DEFAULT_ROTATION,
  starSpeed = 0.7,
  density = 1.0,
  hueShift = 140,
  disableAnimation = false,
  speed = 0.1,
  mouseInteraction = true,
  glowIntensity = 1.5,
  saturation = 0.2,
  mouseRepulsion = true,
  repulsionStrength = 0.04,
  twinkleIntensity = 0.3,
  rotationSpeed = 0.007,
  autoCenterRepulsion = 0,
  transparent = false,
  ...rest
}: GalaxyProps) {
  const ctnDom = useRef<HTMLDivElement>(null);
  const targetMousePos = useRef({
    x: 0.5,
    y: 0.5
  });
  const smoothMousePos = useRef({
    x: 0.5,
    y: 0.5
  });
  const targetMouseActive = useRef(0.0);
  const smoothMouseActive = useRef(0.0);
  const prefersReducedTransparency = usePrefersReducedTransparency();
  const prefersIncreasedContrast = usePrefersIncreasedContrast();
  const doFewerStars = prefersReducedTransparency || prefersIncreasedContrast;
  useEffect(() => {
    if (!ctnDom.current) return;
    const ctn = ctnDom.current;
    const renderer = new Renderer({
      alpha: transparent,
      premultipliedAlpha: false
    });
    const gl = renderer.gl;
    if (transparent) {
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.clearColor(0, 0, 0, 0);
    } else {
      gl.clearColor(0, 0, 0, 1);
    }
    let program: Program | undefined = undefined;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    // stops the canvas from flickering while still letting us resize it
    // I tried it with requestAnimationFrame, but that wouldn't stop the flickering
    // Seems setTimeout manages to win out... this time
    function resizeDebounced() {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        resizeRaw();
        timeout = null;
      }, 5);
    }
    function resizeRaw() {
      const scale = 1;
      const {
        width,
        height
      } = ctn.getBoundingClientRect();
      renderer.setSize(width * scale, height * scale);
      gl.canvas.width = width;
      gl.canvas.height = height;
      if (program) {
        program.uniforms.uResolution.value = new Color(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height);
      }
    }
    const resizeObserver = new ResizeObserver(resizeDebounced);
    resizeObserver.observe(ctn);
    resizeRaw();
    const geometry = new Triangle(gl);
    program = new Program(gl, {
      vertex: vertexShader,
      fragment: getFragmentShader(doFewerStars ? `1` : `3`),
      uniforms: {
        uTime: {
          value: 0
        },
        uResolution: {
          value: new Color(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height)
        },
        uFocal: {
          value: new Float32Array(focal)
        },
        uRotation: {
          value: new Float32Array(rotation)
        },
        uStarSpeed: {
          value: starSpeed
        },
        uDensity: {
          value: density / (doFewerStars ? 2.3 : 1)
        },
        uHueShift: {
          value: hueShift
        },
        uSpeed: {
          value: speed
        },
        uMouse: {
          value: new Float32Array([smoothMousePos.current.x, smoothMousePos.current.y])
        },
        uGlowIntensity: {
          value: glowIntensity
        },
        uSaturation: {
          value: saturation
        },
        uMouseRepulsion: {
          value: mouseRepulsion
        },
        uTwinkleIntensity: {
          value: twinkleIntensity
        },
        uRotationSpeed: {
          value: rotationSpeed
        },
        uRepulsionStrength: {
          value: repulsionStrength
        },
        uMouseActiveFactor: {
          value: 0.0
        },
        uAutoCenterRepulsion: {
          value: autoCenterRepulsion
        },
        uTransparent: {
          value: transparent
        }
      }
    });
    const mesh = new Mesh(gl, {
      geometry,
      program
    });
    let animateId: number;
    const update = (t: number) => {
      animateId = requestAnimationFrame(update);
      if (!disableAnimation) {
        program.uniforms.uTime.value = t * 0.001;
        program.uniforms.uStarSpeed.value = t * 0.001 * starSpeed / 10.0;
      }
      const lerpFactor = 0.05;
      smoothMousePos.current.x += (targetMousePos.current.x - smoothMousePos.current.x) * lerpFactor;
      smoothMousePos.current.y += (targetMousePos.current.y - smoothMousePos.current.y) * lerpFactor;
      smoothMouseActive.current += (targetMouseActive.current - smoothMouseActive.current) * lerpFactor * 0.1;
      program.uniforms.uMouse.value[0] = smoothMousePos.current.x;
      program.uniforms.uMouse.value[1] = smoothMousePos.current.y;
      program.uniforms.uMouseActiveFactor.value = smoothMouseActive.current;
      renderer.render({
        scene: mesh
      });
    };
    animateId = requestAnimationFrame(update);
    ctn.appendChild(gl.canvas);
    function handleMouseMove(e: MouseEvent) {
      const rect = ctn.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1.0 - (e.clientY - rect.top) / rect.height;
      targetMousePos.current = {
        x,
        y
      };
      targetMouseActive.current = 1.0;
    }
    function handleMouseLeave() {
      targetMouseActive.current = 0.0;
    }
    if (mouseInteraction) {
      window.addEventListener("mousemove", handleMouseMove);
      ctn.addEventListener("mouseleave", handleMouseLeave);
    }
    return () => {
      cancelAnimationFrame(animateId);
      window.removeEventListener("resize", resizeDebounced);
      if (mouseInteraction) {
        window.removeEventListener("mousemove", handleMouseMove);
        ctn.removeEventListener("mouseleave", handleMouseLeave);
      }
      ctn.removeChild(gl.canvas);
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [focal, rotation, starSpeed, density, hueShift, disableAnimation, speed, mouseInteraction, glowIntensity, saturation, mouseRepulsion, twinkleIntensity, rotationSpeed, repulsionStrength, autoCenterRepulsion, transparent, doFewerStars]);
  return <GalaxyContainer ref={ctnDom} {...rest} />;
}